/* eslint-disable */
import { CRS_PATH, PATH_DASHBOARD } from '../../../routes/paths';
// components
import SvgIconStyle from '../../../components/SvgIconStyle';
// other
import { getUserPermissions } from 'src/careflow/ph-core/admin/permissions/util/get.user.permissions';
import { NavListProps } from '../../../components/nav-section/type';
import { matchPath } from 'react-router';
import Iconify from 'src/components/Iconify';
import { CCM_ACLS } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { IS_CRS, Modules } from 'src/config';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import React from 'react';

const getIcon = (name: string) => (
  <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const STATIC_MENU_ICONS: any = [
  { path: 'user', icon: 'ic_user', setIcon: true },
  { path: 'banking', icon: 'ic_banking', setIcon: true },
  { path: 'dashboard', icon: 'ic_dashboard_02', setIcon: true },
  { path: 'checklist', icon: 'ic_checklist_03', setIcon: true },
  { path: 'collaboration', icon: 'ic_collaboration', setIcon: true },
  { path: 'record', icon: 'ic_record', setIcon: true },
  { path: 'personalInformation', icon: 'ic_personal_information', setIcon: true },
  { path: 'report', icon: 'ic_report', setIcon: true },
  { path: 'rounding', icon: 'material-symbols:fact-check-outline' },
  { path: 'permissions', icon: 'material-symbols:admin-panel-settings-outline' },
  { path: 'task', icon: 'material-symbols:format-list-bulleted' },
  { path: 'setting', icon: 'ci:settings-filled' },
  { path: 'incident', icon: 'material-symbols:warning-outline' },
  { path: 'people', icon: 'ic:round-people' },
  { path: 'topic', icon: 'material-symbols:topic' },
  { path: 'person', icon: 'mdi:user' },
  { path: 'practitioner', icon: 'ic:round-people' },
  { path: 'tags', icon: 'material-symbols:label-outline' },
  { path: 'fhirOrganization', icon: 'ic:outline-business' },
  { path: 'customFields', icon: 'hugeicons:input-text' },
  { path: 'episodeOfCare', icon: 'material-symbols:folder-outline' },
];

const getIconsByConfig = (iconsFromDb: any) => {
  const iconMap: Record<string, JSX.Element> = {};

  const staticIconMap = STATIC_MENU_ICONS.reduce((acc, { path, icon, setIcon }) => {
    acc[path] = setIcon ? getIcon(icon) : <Iconify icon={icon} />;
    return acc;
  }, {} as Record<string, JSX.Element>);

  if (iconsFromDb && iconsFromDb.length > 0) {
    iconsFromDb.forEach(({ path, icon, properties, setIcon }) => {
      iconMap[path] = setIcon ? getIcon(icon) : <Iconify icon={icon} sx={{ ...properties }} />;
    });
  }

  Object.keys(staticIconMap).forEach((path) => {
    if (!iconMap[path]) {
      iconMap[path] = staticIconMap[path];
    }
  });

  return iconMap;
};

interface SectionPermissions {
  subheader: string;
  itemsByPermission: Record<string, NavListProps>;
  module?: Modules;
}

const mappings = ({ i18n, iconsConfig }: any): SectionPermissions[] => {
  const ICONS = getIconsByConfig(iconsConfig);

  return [
    {
      subheader: 'Admin',
      itemsByPermission: {
        [crsAcls.ADMIN.USERS.MENU]: {
          title: `${i18n('adminNav.users')}`,
          path: PATH_DASHBOARD.user.list,
          icon: ICONS.user,
        },
        [crsAcls.ADMIN.ORGANIZATIONS.MENU]: {
          title: `${i18n('adminNav.organization')}`,
          path: PATH_DASHBOARD.organization.list,
          icon: ICONS.banking,
        },
        [crsAcls.ADMIN.PERMISSIONS.MENU]: {
          title: `${i18n('adminNav.permissions')}`,
          path: PATH_DASHBOARD.general.permissions,
          icon: ICONS.permissions,
        },
        [crsAcls.ADMIN.FHIR_ORGANIZATIONS.MENU]: {
          title: `${i18n('adminNav.fhirOrganization')}`,
          path: PATH_DASHBOARD.fhirOrganization.list,
          icon: ICONS.fhirOrganization,
        },
        [crsAcls.ADMIN.CUSTOM_FIELDS.MENU]: {
          title: `${i18n('adminNav.customFields')}`,
          path: PATH_DASHBOARD.general.customFields,
          icon: ICONS.customFields,
        },
        [crsAcls.ADMIN.WORKFLOW.MENU]: {
          title: `${i18n('adminNav.workflow')}`,
          path: PATH_DASHBOARD.crs.workflow,
          icon: <Iconify icon="material-symbols:account-tree-outline" />,
        },
        [crsAcls.ADMIN.CHECKLIST.MENU]: {
          title: `${i18n('adminNav.checklist')}`,
          path: PATH_DASHBOARD.crs.checklist,
          icon: ICONS.checklist,
        },
      },
    },
    {
      subheader: 'TOOLS',
      module: Modules.CCM,
      itemsByPermission: {
        [CCM_ACLS.TASK_ACTIVITIES.MENU]: {
          title: 'Tasks / Activities',
          path: PATH_DASHBOARD.general.activities,
          icon: ICONS.checklist,
        },
        [CCM_ACLS.CENSUS.name]: {
          title: 'Census',
          path: PATH_DASHBOARD.general.roundings.census,
          icon: <Iconify icon={'icon-park-outline:hospital-bed'} />,
        },
        [CCM_ACLS.ROUNDINGS.name]: {
          title: 'Roundings',
          path: PATH_DASHBOARD.general.roundings.list,
          icon: ICONS.rounding,
          checkActive(pathname) {
            return (
              !!matchPath('/dashboard/roundings', pathname) ||
              !!matchPath('/dashboard/roundings/add-rounding', pathname) ||
              !!matchPath('/dashboard/roundings/:roundingId/edit', pathname)
            );
          },
        },
        [CCM_ACLS.GROUP_SESSIONS.name]: {
          title: 'Group Sessions',
          path: PATH_DASHBOARD.general.groupSessions,
          icon: <Iconify icon={'ic:outline-groups-3'} />,
          checkActive(pathname) {
            return (
              !!matchPath('/dashboard/group-sessions', pathname) ||
              !!matchPath('/dashboard/group-sessions/:groupSessionId/sessions', pathname) ||
              !!matchPath('/dashboard/group-sessions/:groupSessionId/edit', pathname) ||
              !!matchPath(
                '/dashboard/group-sessions/:groupSessionId/sessions/:sessionId/edit',
                pathname
              )
            );
          },
        },
        [CCM_ACLS.MED_PASS.name]: {
          title: 'Med Pass',
          path: PATH_DASHBOARD.general.medPass,
          icon: <Iconify icon={'material-symbols:medication-liquid-outline-sharp'} />,
          checkActive(pathname) {
            return !!matchPath('/dashboard/med-pass', pathname);
          },
        },
        'ccm.reports': {
          title: 'Analytics / Reports',
          path: PATH_DASHBOARD.general.reports,
          icon: ICONS.report,
          checkActive(pathname) {
            return (
              !!matchPath('/dashboard/patient/:patientId/reports/*', pathname) ||
              !!matchPath('/dashboard/reports/*', pathname)
            );
          },
        },
      },
    },
    {
      subheader: 'PATIENT DETAILS',
      module: Modules.CCM,
      itemsByPermission: {
        [CCM_ACLS.PATIENt_DEMOGRAPHICS.name]: {
          title: 'Patient Demographics',
          path: PATH_DASHBOARD.general.patientDemographics,
          icon: ICONS.personalInformation,
          checkActive(pathname) {
            return (
              !!matchPath('/dashboard/patient/:patientId/patient-demographics/*', pathname) ||
              !!matchPath('/dashboard/patient-demographics/*', pathname)
            );
          },
        },
        [CCM_ACLS.HEALTH_RECORD.name]: {
          title: 'Health Record',
          path: PATH_DASHBOARD.general.healthRecords,
          icon: ICONS.record,
          checkActive(pathname) {
            return (
              !!matchPath('/dashboard/patient/:patientId/health-record/*', pathname) ||
              !!matchPath('/dashboard/health-record/*', pathname)
            );
          },
        },
        [CCM_ACLS.CAREPLAN.name]: {
          title: 'Care Record',
          path: PATH_DASHBOARD.general.careRecord,
          icon: ICONS.collaboration,
          checkActive(pathname) {
            return (
              !!matchPath('/dashboard/patient/:patientId/care-record/*', pathname) ||
              !!matchPath('/dashboard/care-record/*', pathname)
            );
          },
        },
      },
    },
    {
      subheader: i18n('menu_title', 'crs'),
      module: Modules.CRS,
      itemsByPermission: {
        [crsAcls.CRS.DASHBOARD.MENU]: {
          title: `${i18n('menu.myDashboard', 'crs')}`,
          path: PATH_DASHBOARD.general.app,
          icon: ICONS.dashboard,
        },
        [crsAcls.CRS.PATIENT.MENU]: {
          title: `${i18n('menu.patient', 'crs')}`,
          path: PATH_DASHBOARD.crs.patient.list.value,
          icon: ICONS.personalInformation,
        },
        [crsAcls.CRS.REFERRAL.MENU]: {
          title: `${i18n('menu.inboundReferral', 'crs')}`,
          path: PATH_DASHBOARD.crs.referral,
          icon: ICONS.collaboration,
        },
        [crsAcls.CRS.CASE.MENU]: {
          title: `${i18n('menu.case', 'crs')}`,
          path: PATH_DASHBOARD.crs.case,
          icon: ICONS.checklist,
        },
        [crsAcls.CRS.TASK.MENU]: {
          title: `${i18n('menu.task', 'crs')}`,
          path: PATH_DASHBOARD.crs.task,
          icon: ICONS.task,
        },
      },
    },
    {
      subheader: 'Care Coordination Admin',
      module: Modules.CRS,
      itemsByPermission: {
        [crsAcls.CRS.PLAN_DEFINITION.MENU]: {
          title: `${i18n('menu.planDefinition','crs')}`,
          path: PATH_DASHBOARD.crs.planDefinitions,
          icon: ICONS.topic,
        },
        [crsAcls.CRS.ACTIVITY_DEFINITION.MENU]: {
          title: `${i18n('menu.activityDefinition','crs')}`,
          path: PATH_DASHBOARD.crs.activityDefinitions,
          icon: ICONS.setting,
        },
        [crsAcls.CRS.PRACTITIONER.MENU]: {
          title: `${i18n('menu.practitioner', 'crs')}`,
          path: CRS_PATH.provider.list,
          icon: ICONS.practitioner,
        },
        [crsAcls.CRS.TAGS.MENU]: {
          title: `${i18n('menu.tags','crs')}`,
          path: PATH_DASHBOARD.crs.tags,
          icon: <Iconify icon="material-symbols:label-outline" />,
        },
        [crsAcls.CRS.EPISODE_OF_CARE.MENU]: {
          title: 'Episode Of Care',
          path: PATH_DASHBOARD.crs.episodeOfCare.list,
          icon: ICONS.episodeOfCare,
        },
      },
    },
    {
      subheader: 'YoCare',
      module: Modules.CRS,
      itemsByPermission: {
        [crsAcls.YOCARE.USERS.MENU]: {
          title: `${i18n('yocareNav.users')}`,
          path: PATH_DASHBOARD.yoCare.users,
          icon: ICONS.person,
        },
        [crsAcls.YOCARE.PERSONS.MENU]: {
          title: `${i18n('yocareNav.persons')}`,
          path: PATH_DASHBOARD.yoCare.persons,
          icon: ICONS.people,
        },
        [crsAcls.YOCARE.DATASYSTEMS.MENU]: {
          title: `${i18n('yocareNav.dataSystems')}`,
          path: PATH_DASHBOARD.yoCare.dataSystems,
          icon: ICONS.topic,
        },
      },
    },
    {
      subheader: 'engagement hub',
      module: Modules.CRS,
      itemsByPermission: {
        [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.MENU]: {
          title: `${i18n('patientEngagement.menuTitle', 'engagement')}`,
          path: PATH_DASHBOARD.engagementHub.patientEngagement,
          icon: ICONS.collaboration,
        },
      },
    },
    {
      subheader: 'data quality',
      module: Modules.CRS,
      itemsByPermission: {
        [crsAcls.DATA_QUALITY.INCIDENT.MENU]: {
          title: `${i18n('menu.incident', 'crs')}`,
          path: PATH_DASHBOARD.dataQuality.incident,
          icon: ICONS.incident,
        },
      },
    },
  ];
};

export interface Section {
  subheader: string;
  items: NavListProps[];
}

const getNavConfig = (timeTrackingEnabled = false): Section[] => {
  const { i18n } = useLocales();
  const { componentsData } = useTenantConfigData();
  const iconsConfig = componentsData?.menu?.icons ?? [];

  const userPermissions = getUserPermissions();
  const mappingsByModule = IS_CRS
    ? mappings({ i18n, iconsConfig }).filter(
        ({ module }) => module === Modules.CRS || module === undefined
      )
    : mappings({ i18n, iconsConfig });
  const sections = mappingsByModule.reduce((sections, sectionPermissions) => {
    const permissions = Object.keys(sectionPermissions.itemsByPermission);
    const allowedPermissions = permissions.filter((permission) =>
      userPermissions.isAllowed(permission)
    );
    if (allowedPermissions.length > 0) {
      sections.push({
        subheader: sectionPermissions.subheader,
        items: allowedPermissions.map(
          (permission) => sectionPermissions.itemsByPermission[permission]
        ),
      });
    }
    return sections;
  }, [] as Section[]);
  if (timeTrackingEnabled) {
    sections.push({
      subheader: 'Time tracking',
      items: [{
        title: 'Time entries',
        path: PATH_DASHBOARD.general.timeEntries,
        icon: <Iconify icon="material-symbols:history-rounded" />,
      }, {
        title: 'Timesheets',
        path: PATH_DASHBOARD.general.timesheets,
        icon: <Iconify icon="material-symbols:overview-outline-rounded" />,
      }, {
        title: 'Administration',
        path: PATH_DASHBOARD.general.timeTrackingAdministration,
        icon: <Iconify icon="material-symbols:admin-panel-settings-outline" />,
      }],
    });
  }
  return sections;
};

export default getNavConfig;
